import { usePricesHooks } from '../../../modules/operations'
import { ProductPrices, useProductLoaders, useProductPricesLoaders, useProductPricesBuilders, useProductPricesActions } from '../../../modules/products'
import { dedupe } from '../../../utils/listUtils'
import { isDefined, round } from '../../../utils/typeUtils'
import { FieldType } from '../../ui'
import { CsvImportResult, CsvRow } from '../types'
import { FILE_ROW_KEY, useImportCsv } from './importCsv'
import { ImportPriceField, ProductPricesTemplateColumn } from './types'

export const useImportProductPrices = () => {
    const importCsv = useImportCsv()
    const pricesHooks = usePricesHooks()
    
    const { buildProductPrices } = useProductPricesBuilders()
    
    const productLoaders = useProductLoaders()
    const productPricesLoaders = useProductPricesLoaders()

    const { saveProductPricesList } = useProductPricesActions()

    const columns = {
        supplierCode: {
            name: ProductPricesTemplateColumn.SUPPLIER_CODE,
            type: FieldType.TEXT
        },
        purchasePrice: {
            name: ProductPricesTemplateColumn[ImportPriceField.PURCHASE_PRICE],
            type: FieldType.NUMBER
        },
        priceYield: {
            name: ProductPricesTemplateColumn[ImportPriceField.PRICE_YIELD],
            type: FieldType.NUMBER
        },
        price: {
            name: ProductPricesTemplateColumn[ImportPriceField.PRICE],
            type: FieldType.NUMBER
        },
        priceVat: {
            name: ProductPricesTemplateColumn[ImportPriceField.PRICE_VAT],
            type: FieldType.NUMBER
        }
    }

    const importData = (priceFieldValue: ImportPriceField) => async (rows: CsvRow[]): Promise<CsvImportResult> => {
        const { loadSupplierCodesProducts } = productLoaders()
        const dedupedRows = dedupe(rows, (a, b) => a.supplierCode === b.supplierCode)
        const supplierCodes = dedupedRows
            .map(row => row.supplierCode)
            .filter(supplierCode => supplierCode && isDefined(supplierCode))
            .map(supplierCode => supplierCode! as string)
        const products = await loadSupplierCodesProducts(dedupe(supplierCodes))
        const { loadProductsPrices} = productPricesLoaders()
        const productIds = products.map(product => product.id)
        const productPricesList = await loadProductsPrices(productIds, true)

        const updatedProductPricesList: ProductPrices[] = []
        const importedRows: any[] = []
        const unimportedRows: any[] = []
        for (const row of dedupedRows) {
            const supplierCode = row.supplierCode
            const purchasePrice = row.purchasePrice
            const priceYieldRaw = row.priceYield as number || null
            const priceYield = isDefined(priceYieldRaw) ? round(priceYieldRaw! * 100) : null
            const price = row.price as number || null
            const priceVat = row.priceVat as number || null

            const product = products.find(product => product.supplierCode === supplierCode)
            const prices = productPricesList.find(prices => prices.productId === product?.id)

            // Si el código no existe no hay nombre para mostrar: se muestra el código para poder ubicarlo
            const productLabel = product?.name || (supplierCode ? `${supplierCode} (código inexistente)` : 'Sin código interno')
            let resultRow: CsvRow = {
                rowIndex: row[FILE_ROW_KEY],
                supplierCode,
                product: productLabel
            }

            const importRow = (priceField: ImportPriceField) => {
                if (priceField === ImportPriceField.PRICE_VAT) {
                    const priceWithoutVat = priceVat! / (1 + (pricesHooks.product.vatTaxYield(product!.id) / 100))
                    const saleYield = pricesHooks.saleYield(prices!.purchasePrice, priceWithoutVat!)
                    const updatedPrices: ProductPrices = buildProductPrices({
                        ...prices,
                        saleYield
                    })
                    updatedProductPricesList.push(updatedPrices)
                    resultRow = {
                        ...resultRow,
                        priceVat: priceVat!
                    }
                    importedRows.push(resultRow)
                } else if (priceField === ImportPriceField.PRICE) {
                    const saleYield = pricesHooks.saleYield(prices!.purchasePrice, price!)
                    const updatedPrices: ProductPrices = buildProductPrices({
                        ...prices,
                        saleYield
                    })
                    updatedProductPricesList.push(updatedPrices)
                    resultRow = {
                        ...resultRow,
                        price: price!
                    }
                    importedRows.push(resultRow)
                } else if (priceField === ImportPriceField.PRICE_YIELD) {
                    const updatedPrices: ProductPrices = buildProductPrices({
                        ...prices,
                        saleYield: priceYield!
                    })
                    updatedProductPricesList.push(updatedPrices)
                    resultRow = {
                        ...resultRow,
                        priceYield: priceYield!
                    }
                    importedRows.push(resultRow)
                } else if (priceField === ImportPriceField.PURCHASE_PRICE) {
                    const updatedPrices: ProductPrices = buildProductPrices({
                        ...prices,
                        purchasePrice: purchasePrice!
                    })
                    updatedProductPricesList.push(updatedPrices)
                    resultRow = {
                        ...resultRow,
                        purchasePrice: purchasePrice!
                    }
                    importedRows.push(resultRow)
                }
            }

            const fields = [
                { field: ImportPriceField.PRICE_VAT, value: priceVat },
                { field: ImportPriceField.PRICE, value: price },
                { field: ImportPriceField.PRICE_YIELD, value: priceYield },
                { field: ImportPriceField.PURCHASE_PRICE, value: purchasePrice },
            ]
            const sortedFields = [
                fields.find(f => f.field === priceFieldValue)!,
                ...fields.filter(f => f.field !== priceFieldValue)
            ]
            const [field1, field2, field3, field4] = sortedFields

            // Las filas sin ningún valor nuevo se ignoran sin reportarse, así la plantilla completa se puede subir tal cual
            if (sortedFields.every(f => !isDefined(f.value))) continue

            if (!product || !prices) {
                const fileValues: Record<string, any> = {}
                fields.filter(f => isDefined(f.value)).forEach(f => fileValues[f.field] = f.value)
                unimportedRows.push({ ...resultRow, ...fileValues })
            } else if (isDefined(field1.value)) {
                importRow(field1.field)
            } else if (isDefined(field2.value)) {
                importRow(field2.field)
            } else if (isDefined(field3.value)) {
                importRow(field3.field)
            } else {
                importRow(field4.field)
            }
        }
        
        await saveProductPricesList(updatedProductPricesList)

        return {
            importedRows,
            unimportedRows
        }
    }

    return (file: File, priceFieldValue: ImportPriceField) =>
        importCsv(file, columns, importData(priceFieldValue))
}
